import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AssetsModule } from '@app/_assets';
import { rxResourceFixed } from '@app/_core/rx-resource-fixed';
import { TranslateModule } from '@app/_modules/translate';
import { TranslateService } from '@app/_modules/translate/translate.service';
import { AppPipes } from '@app/_pipes';
import { AlertsService, AuthService, SettingsService } from '@app/_services';
import { LoaderComponent } from '@app/components/loader';
import { catchError, map, of, tap } from 'rxjs';

@Component({
	selector: 'user-modal',
	templateUrl: './user-modal.template.html',
	styleUrl: './user-modal.styles.scss',
	//standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [AssetsModule, TranslateModule, AppPipes, CommonModule, LoaderComponent],
})
export class UserModalComponent {
	private readonly settingsService = inject(SettingsService);
	private readonly httpClient = inject(HttpClient);
	private readonly alertsService = inject(AlertsService);
	private readonly translateService = inject(TranslateService);
	protected authService = inject(AuthService);

	protected readonly personalDataFields = [
		{ key: 'isikukood' },
		{ key: 'synniKp' },
		{ key: 'elukohamaa' },
		{ key: 'rrElukoht' },
		{ key: 'kodakondsus' },
		{ key: 'elamisluba' },
		{
			key: 'oppelaenOigus',
			value: (field?: { oigus?: 'Jah' }) =>
				field?.oigus === 'Jah' && this.translateService.get('true'),
		},
	];

	userDataResource = rxResourceFixed({
		params: () => ({
			valid: this.authService.hasValidEhisAuth(),
		}),
		stream: () => {
			if (!this.authService.hasValidEhisAuth()) {
				return;
			}
			return this.getUserData();
		},
	});

	getUserData() {
		return this.httpClient
			.get(
				`${this.settingsService.url}/dashboard/eeIsikukaart/personal_data?_format=json`,
			)
			.pipe(
				catchError(() => of({ error: true })),
				map((res: any) => {
					if (res.error) {
						this.alertsService.error(
							'errors.personal_data_missing',
							'personalData',
							'personalData',
							false,
							false,
						);
						return {} as {
							useEhisNames?: boolean;
							eesnimi?: string;
							perenimi?: string;
						};
					} else {
						return res?.value?.isikuandmed as {
							useEhisNames?: boolean;
							eesnimi?: string;
							perenimi?: string;
						};
					}
				}),
				tap((res) => {
					/**
					 * Update user in auth service if names have changed
					 */
					if (res?.useEhisNames) {
						this.authService.setUser({
							firstname: res?.eesnimi,
							lastname: res?.perenimi,
							usingEhisNames: true,
						});
					}
				}),
			);
	}
}
